import { useMemo } from 'react'
import { Loader2, RefreshCw } from 'lucide-react'
import {
  useGetGraphQuery,
  useListConditionApplicabilityQuery,
  useUpdateConditionApplicabilityMutation,
  type ConditionApplicability,
  type GraphQueryParams,
  type OntologyGraphNode
} from '@axilon-apps/domains-ontology-ui-state'

import { Badge } from '../../../components/ui/badge'
import { Button } from '../../../components/ui/button'
import { Card, CardContent } from '../../../components/ui/card'

const CONDITIONS_QUERY: GraphQueryParams = {
  nodeTypes: ['FaultSymptom'],
  nodeLimit: 1000,
  relationshipLimit: 0
}

function nodeText(node: OntologyGraphNode, key: string): string | undefined {
  const value = node[key]
  return typeof value === 'string' && value.trim() ? value.trim() : undefined
}

export function usefulnessLabel(row: Pick<ConditionApplicability, 'asked' | 'discriminated'>) {
  if (row.asked === 0) return 'not asked yet'
  return `${row.discriminated}/${row.asked} discriminating`
}

export function ConditionVocabularyCard() {
  const conditionsQuery = useGetGraphQuery(CONDITIONS_QUERY)
  const applicabilityQuery = useListConditionApplicabilityQuery()
  const [updateApplicability, updateState] = useUpdateConditionApplicabilityMutation()
  const conditions = useMemo(
    () =>
      [...(conditionsQuery.data?.nodes ?? [])]
        .filter((node) => node.label === 'FaultSymptom')
        .sort((a, b) =>
          (nodeText(a, 'symptom') ?? nodeText(a, 'name') ?? a._id).localeCompare(
            nodeText(b, 'symptom') ?? nodeText(b, 'name') ?? b._id
          )
        ),
    [conditionsQuery.data?.nodes]
  )
  const rowsByCondition = useMemo(() => {
    const grouped = new Map<string, ConditionApplicability[]>()
    for (const row of applicabilityQuery.data ?? []) {
      const rows = grouped.get(row.condition_id) ?? []
      rows.push(row)
      grouped.set(row.condition_id, rows)
    }
    return grouped
  }, [applicabilityQuery.data])

  const loading = conditionsQuery.isLoading || applicabilityQuery.isLoading
  const error = conditionsQuery.isError || applicabilityQuery.isError
  const refetch = () => {
    void conditionsQuery.refetch()
    void applicabilityQuery.refetch()
  }

  return (
    <Card className="gap-3 py-4">
      <CardContent className="flex flex-col gap-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h3 className="text-sm font-semibold text-ink">Condition vocabulary</h3>
            <p className="mt-0.5 text-xs text-ink-3">
              Conditions and the equipment types where each check is useful.
            </p>
          </div>
          <Button size="xs" variant="outline" onClick={refetch} disabled={loading}>
            <RefreshCw className={loading ? 'animate-spin' : undefined} />
            Refresh
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 py-2.5 text-xs text-ink-3">
            <Loader2 className="size-3 animate-spin" />
            Loading vocabulary…
          </div>
        ) : null}
        {error ? (
          <div role="alert" className="rounded-md bg-st-down-bg px-3 py-2 text-xs text-st-down">
            Failed to load the condition vocabulary.
          </div>
        ) : null}

        {!loading && !error && conditions.length === 0 ? (
          <p className="py-2.5 text-xs text-ink-3">No conditions have been defined yet.</p>
        ) : null}

        <div className="flex flex-col gap-2">
          {conditions.map((condition) => {
            const id = nodeText(condition, 'id') ?? condition._id
            const name = nodeText(condition, 'symptom') ?? nodeText(condition, 'name') ?? id
            const kind = nodeText(condition, 'kind') ?? 'other'
            const rows = rowsByCondition.get(id) ?? []
            return (
              <section key={id} className="rounded-md border border-line p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-ink">{name}</span>
                  <Badge variant="secondary" className="text-10 capitalize">
                    {kind.replace(/_/g, ' ')}
                  </Badge>
                  <span className="ml-auto font-mono text-10 text-ink-4">{id}</span>
                </div>
                {rows.length === 0 ? (
                  <p className="mb-0 mt-2 text-xs text-ink-3">
                    No equipment applicability has been recorded.
                  </p>
                ) : (
                  <ul className="m-0 mt-2 flex list-none flex-col gap-1.5 p-0">
                    {rows.map((row) => (
                      <li
                        key={row.id}
                        className="flex flex-wrap items-start gap-x-2 gap-y-1 rounded bg-bg-sunk/50 px-2.5 py-2 text-xs"
                      >
                        <span className="font-medium text-ink">{row.equipment_type}</span>
                        <Badge variant="outline" className="text-10 capitalize">
                          {row.state}
                        </Badge>
                        <Badge variant="outline" className="text-10">
                          {row.source.replace(/_/g, ' ')}
                        </Badge>
                        <span className="text-ink-3">{usefulnessLabel(row)}</span>
                        <span className="w-full text-ink-2">{row.how_to_check}</span>
                        <Button
                          type="button"
                          size="xs"
                          variant="ghost"
                          disabled={updateState.isLoading}
                          onClick={() =>
                            void updateApplicability({
                              id: row.id,
                              state: row.state === 'retired' ? 'active' : 'retired'
                            })
                          }
                        >
                          {row.state === 'retired' ? 'Reactivate' : 'Retire'}
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}
